import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ProtectedRoute } from '@/app/routes/ProtectedRoute';
import { RoleGuard } from '@/app/routes/RoleGuard';
import { DeveloperGuard } from '@/app/routes/DeveloperGuard';
import { ModuleGuard } from '@/app/routes/ModuleGuard';
import { paths } from '@/app/routes/routePaths';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { FullPageSpinner } from '@/components/common/FullPageSpinner';
import { PERMISSIONS } from '@/config/constants';

// Every page is code-split with React.lazy. A dashboard the size of an
// EDMS (admin panels, viewers, approval flows) should never ship as one
// bundle — this keeps first paint fast and lets heavy pieces (PDF
// viewer, admin screens) load only when visited.
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const SearchPage = lazy(() => import('@/pages/dashboard/SearchPage'));
const DocumentsListPage = lazy(() => import('@/pages/documents/DocumentsListPage'));
const DocumentDetailPage = lazy(() => import('@/pages/documents/DocumentDetailPage'));
const DocumentUploadPage = lazy(() => import('@/pages/documents/DocumentUploadPage'));
const FolderPage = lazy(() => import('@/pages/folders/FolderPage'));
const ApprovalsPage = lazy(() => import('@/pages/approvals/ApprovalsPage'));
const DigitizationCenterPage = lazy(() => import('@/pages/digitization/DigitizationCenterPage'));
const MasterRecordForm = lazy(() => import('@/pages/digitization/MasterRecordForm'));
const MasterRecordDetailPage = lazy(() => import('@/pages/digitization/MasterRecordDetailPage'));
const DocumentStructurePage = lazy(() => import('@/pages/digitization/DocumentStructurePage'));
const DocumentViewerPage = lazy(() => import('@/pages/digitization/DocumentViewerPage').then(m => ({ default: m.DocumentViewerPage })));
const QualityControlPage = lazy(() => import('@/pages/digitization/QualityControlPage'));
const UsersAdminPage = lazy(() => import('@/pages/admin/UsersAdminPage'));
const UserFormPage = lazy(() => import('@/pages/admin/UserFormPage').then(m => ({ default: m.UserFormPage })));
const RolesAdminPage = lazy(() => import('@/pages/admin/RolesAdminPage'));
const RolePermissionsPage = lazy(() => import('@/pages/admin/RolePermissionsPage'));
const DepartmentsAdminPage = lazy(() => import('@/pages/admin/DepartmentsAdminPage'));
const ActionControlPage = lazy(() => import('@/pages/admin/ActionControlPage'));
const WorkflowsAdminPage = lazy(() => import('@/pages/admin/WorkflowsAdminPage'));
const WorkflowBuilderPage = lazy(() => import('@/pages/admin/WorkflowBuilderPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage').then(module => ({ default: module.ProfilePage })));
const AuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'));
const TrashPage = lazy(() => import('@/pages/admin/TrashPage'));
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/errors/ForbiddenPage'));

// Platform Admin
import { PlatformAdminGuard } from '@/app/routes/PlatformAdminGuard';
const OrganizationsListPage = lazy(() => import('@/pages/platform/OrganizationsListPage').then(m => ({ default: m.OrganizationsListPage })));
const CreateOrganizationPage = lazy(() => import('@/pages/platform/CreateOrganizationPage').then(m => ({ default: m.CreateOrganizationPage })));
const OrganizationDetailsPage = lazy(() => import('@/pages/platform/OrganizationDetailsPage').then(m => ({ default: m.OrganizationDetailsPage })));

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<FullPageSpinner />}>{element}</Suspense>;
}

const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [{ path: paths.login, element: withSuspense(<LoginPage />) }],
  },
  {
    element: <ProtectedRoute />, // must be logged in
    children: [
      { path: '/documents/viewer/:volumeId', element: withSuspense(<DocumentViewerPage />) },
      {
        element: <AppLayout />, // sidebar + topbar shell
        children: [
          { path: paths.dashboard, element: <ModuleGuard module="dashboard">{withSuspense(<DashboardPage />)}</ModuleGuard> },
          { path: paths.search, element: <ModuleGuard module="documents">{withSuspense(<SearchPage />)}</ModuleGuard> },
          { path: paths.documents, element: <ModuleGuard module="documents">{withSuspense(<DocumentsListPage />)}</ModuleGuard> },
          { path: paths.documentDetail(':documentId'), element: <ModuleGuard module="documents">{withSuspense(<DocumentDetailPage />)}</ModuleGuard> },
          { path: paths.documentUpload, element: <ModuleGuard module="documents">{withSuspense(<DocumentUploadPage />)}</ModuleGuard> },
          { path: paths.folder(':folderId'), element: <ModuleGuard module="documents">{withSuspense(<FolderPage />)}</ModuleGuard> },
          { path: paths.approvals, element: <ModuleGuard module="approvals">{withSuspense(<ApprovalsPage />)}</ModuleGuard> },
          { path: paths.digitization, element: <ModuleGuard module="digitization">{withSuspense(<DigitizationCenterPage />)}</ModuleGuard> },
          { path: paths.digitizationCreateMaster, element: <ModuleGuard module="digitization">{withSuspense(<MasterRecordForm />)}</ModuleGuard> },
          { path: paths.digitizationMasterDetail(':id'), element: <ModuleGuard module="digitization">{withSuspense(<MasterRecordDetailPage />)}</ModuleGuard> },
          { path: paths.digitizationMasterStructure(':id'), element: <ModuleGuard module="digitization">{withSuspense(<DocumentStructurePage />)}</ModuleGuard> },
          { path: paths.qualityControl, element: <ModuleGuard module="quality_control">{withSuspense(<QualityControlPage />)}</ModuleGuard> },
          { path: paths.profile, element: withSuspense(<ProfilePage />) },
          {
            // Admin routes with specific permissions
            children: [
              { path: paths.adminUsers, element: <ModuleGuard module="users"><RoleGuard requiredPermissions={['users.view']}>{withSuspense(<UsersAdminPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminUserCreate, element: <ModuleGuard module="users"><RoleGuard requiredPermissions={['users.create']}>{withSuspense(<UserFormPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminUserEdit(':id'), element: <ModuleGuard module="users"><RoleGuard requiredPermissions={['users.edit']}>{withSuspense(<UserFormPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminRoles, element: <ModuleGuard module="roles"><RoleGuard requiredPermissions={['roles.view']}>{withSuspense(<RolesAdminPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminRolePermissions(':id'), element: <ModuleGuard module="roles"><RoleGuard requiredPermissions={['roles.edit']}>{withSuspense(<RolePermissionsPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminDepartments, element: <ModuleGuard module="departments"><RoleGuard requiredPermissions={['departments.view']}>{withSuspense(<DepartmentsAdminPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminWorkflows, element: <ModuleGuard module="workflows"><RoleGuard requiredPermissions={['workflows.view']}>{withSuspense(<WorkflowsAdminPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminWorkflowBuilder(':id'), element: <ModuleGuard module="workflows"><RoleGuard requiredPermissions={['workflows.edit']}>{withSuspense(<WorkflowBuilderPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminAudit, element: <ModuleGuard module="audit_logs"><RoleGuard requiredPermissions={['audit_logs.view']}>{withSuspense(<AuditLogsPage />)}</RoleGuard></ModuleGuard> },
              { path: paths.adminTrash, element: <ModuleGuard module="trash"><RoleGuard requiredPermissions={['trash.view']}>{withSuspense(<TrashPage />)}</RoleGuard></ModuleGuard> },
            ],
          },
          {
            element: <DeveloperGuard />,
            children: [
              { path: paths.adminActionControl, element: withSuspense(<ActionControlPage />) },
            ],
          },
          {
            element: <PlatformAdminGuard />,
            children: [
              { path: '/platform/organizations', element: withSuspense(<OrganizationsListPage />) },
              { path: '/platform/organizations/create', element: withSuspense(<CreateOrganizationPage />) },
              { path: '/platform/organizations/:id', element: withSuspense(<OrganizationDetailsPage />) },
              { path: paths.settings, element: withSuspense(<SettingsPage />) },
            ],
          },
          { path: paths.forbidden, element: withSuspense(<ForbiddenPage />) },
          { path: '*', element: withSuspense(<NotFoundPage />) },
        ],
      },
    ],
  },
], { basename: '/dms' });

export function AppRouter() {
  return <RouterProvider router={router} />;
}
